import { useEffect, useState, type MouseEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Loader2, CheckCircle2, Bell, X, ArrowRight,
} from 'lucide-react';
import {
  fetchAlerts, fetchAlertDetail, acknowledgeAlert, resolveAlert,
  type AlertRow,
} from '@/lib/api';
import {
  ALERT_SEVERITY_LABELS, ALERT_STATUS_LABELS, alertChargerAndSite, alertChargerName,
  alertRuleName, formatAlertLifecycleTime, formatAlertTime, siteZoneMissing,
} from '@/lib/alertPresentation';
import { useIsMobile } from '@/hooks/useViewport';
import styles from './AlertsPage.module.css';

/**
 * Phase 5a Item 10.4 — admin Alerts page.
 *
 * List view + detail drawer + ack/resolve actions. Drives the
 * operator-facing alert lifecycle: detected → notified →
 * action_pending → resolved.
 *
 * Scoping is server-enforced (tenant filter on the GET /admin/alerts
 * route); super_admin sees all tenants, others see their own.
 *
 * Every row names its charger and site, and times are the site's local
 * time. On phones each alert is a card that opens the charger.
 *
 * /alerts/:id (the link in alert notifications and emails) is this page
 * with that alert's drawer open over the list.
 */

interface AlertsPageProps {
  onNavigate: (path: string) => void;
  /** The alert whose drawer opens over the list, from /alerts/:id. */
  initialOpenId?: string;
}

type StatusFilter = AlertRow['status'] | 'open';

const STATUS_OPTIONS: AlertRow['status'][] = ['detected', 'notified', 'action_pending', 'escalated', 'resolved'];
const SEVERITY_OPTIONS: AlertRow['severity'][] = ['info', 'warning', 'critical'];

function chargerPath(a: AlertRow): string {
  return `/chargers/${a.charger_id}`;
}

/** Detected time in the site zone; a loaded site without one says so. */
function DetectedAt({ alert }: { alert: AlertRow }) {
  const t = formatAlertTime(alert.detected_at, alert.site?.timezone);
  return (
    <>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{t.text}</span>
      {siteZoneMissing(alert.site) && (
        <span style={{ display: 'block', fontSize: 11, color: 'var(--color-tx-m)' }}>Site time zone not set</span>
      )}
    </>
  );
}

function SeverityBadge({ severity }: { severity: AlertRow['severity'] }) {
  const colorMap = {
    info: 'var(--color-tx-m)',
    warning: 'var(--evi-warning, #f59e0b)',
    critical: 'var(--evi-error, #ef4444)',
  };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 999,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
      background: `color-mix(in srgb, ${colorMap[severity]} 16%, transparent)`,
      color: colorMap[severity],
    }}>
      {ALERT_SEVERITY_LABELS[severity]}
    </span>
  );
}

function StatusBadge({ status }: { status: AlertRow['status'] }) {
  const colorMap: Record<AlertRow['status'], string> = {
    detected: 'var(--color-tx-m)',
    notified: 'var(--evi-warning, #f59e0b)',
    action_pending: 'var(--evi-accent, #2ECC71)',
    resolved: 'var(--color-tx-m)',
    escalated: 'var(--evi-error, #ef4444)',
  };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      fontSize: 11,
      borderRadius: 4,
      whiteSpace: 'nowrap',
      background: `color-mix(in srgb, ${colorMap[status]} 16%, transparent)`,
      color: colorMap[status],
    }}>
      {ALERT_STATUS_LABELS[status]}
    </span>
  );
}

export function AlertsPage({ onNavigate, initialOpenId }: AlertsPageProps) {
  const isMobile = useIsMobile();
  // "All open" is every alert not yet resolved (fetchAlerts sends the four open statuses).
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [severityFilter, setSeverityFilter] = useState<AlertRow['severity'] | ''>('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(initialOpenId ?? null);
  // A new /alerts/:id (a notification followed while on the page) opens its alert.
  useEffect(() => {
    if (initialOpenId) setOpenId(initialOpenId);
  }, [initialOpenId]);

  // Closing an alert opened from its link returns the address to the list.
  const closeDrawer = () => {
    setOpenId(null);
    if (initialOpenId) onNavigate('/alerts');
  };

  const listQ = useQuery({
    queryKey: ['alerts', statusFilter, severityFilter, page],
    queryFn: () => fetchAlerts({
      status: statusFilter,
      severity: severityFilter || undefined,
      page,
      page_size: 25,
    }),
  });

  // In-app navigation, keeping the href for open-in-new-tab.
  const openCharger = (e: MouseEvent, a: AlertRow) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    onNavigate(chargerPath(a));
  };

  const rows = listQ.data?.data ?? [];

  return (
    <div style={{ padding: isMobile ? 16 : '24px 28px' }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>
          <Bell size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Alerts
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-tx-m)' }}>
          Predictive-maintenance alerts produced by the rule engine.
        </p>
      </header>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, fontSize: 13 }}>
        <label>
          Status:{' '}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setPage(1); }}
          >
            <option value="open">All open</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{ALERT_STATUS_LABELS[s]}</option>)}
          </select>
        </label>
        <label>
          Severity:{' '}
          <select
            value={severityFilter}
            onChange={(e) => { setSeverityFilter(e.target.value as AlertRow['severity'] | ''); setPage(1); }}
          >
            <option value="">All</option>
            {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{ALERT_SEVERITY_LABELS[s]}</option>)}
          </select>
        </label>
      </div>

      {listQ.isLoading && (
        <p><Loader2 size={14} className="spin" /> Loading…</p>
      )}
      {listQ.isError && (
        <p style={{ color: 'var(--evi-error, #ef4444)' }}>
          <AlertTriangle size={14} /> Could not load alerts.
        </p>
      )}
      {listQ.data && (
        <>
          {isMobile ? (
            <div className={styles.cards}>
              {rows.map((a) => {
                const body = (
                  <>
                    <span className={styles.rule}>{alertRuleName(a.rule_id)}</span>
                    <span className={styles.subject}>{alertChargerAndSite(a)}</span>
                    <span className={styles.time}><DetectedAt alert={a} /></span>
                    <span className={styles.chips}>
                      <SeverityBadge severity={a.severity} />
                      <StatusBadge status={a.status} />
                    </span>
                    <span className={styles.message}>{a.message}</span>
                  </>
                );
                return (
                  <article key={a._id} className={styles.card}>
                    {a.charger ? (
                      <a
                        href={chargerPath(a)}
                        onClick={(e) => openCharger(e, a)}
                        className={styles.cardLink}
                      >
                        {body}
                      </a>
                    ) : (
                      <div className={styles.cardLink}>{body}</div>
                    )}
                    <button type="button" className={styles.detailsBtn} onClick={() => setOpenId(a._id)}>
                      Details <ArrowRight size={11} style={{ verticalAlign: 'middle' }} />
                    </button>
                  </article>
                );
              })}
              {rows.length === 0 && (
                <div className={styles.empty}>No alerts matching the current filters.</div>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))', textAlign: 'left' }}>
                    <th style={{ padding: 8 }}>Detected</th>
                    <th style={{ padding: 8 }}>Rule</th>
                    <th style={{ padding: 8 }}>Charger</th>
                    <th style={{ padding: 8 }}>Site</th>
                    <th style={{ padding: 8 }}>Severity</th>
                    <th style={{ padding: 8 }}>Status</th>
                    <th style={{ padding: 8 }}>Message</th>
                    <th style={{ padding: 8 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a._id} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                      <td style={{ padding: 8, color: 'var(--color-tx-m)', whiteSpace: 'nowrap' }}><DetectedAt alert={a} /></td>
                      <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{alertRuleName(a.rule_id)}</td>
                      <td style={{ padding: 8, minWidth: 120 }}>
                        {a.charger
                          ? <a href={chargerPath(a)} onClick={(e) => openCharger(e, a)}>{a.charger.name}</a>
                          : <span style={{ color: 'var(--color-tx-m)' }}>{alertChargerName(a)}</span>}
                      </td>
                      <td style={{ padding: 8, minWidth: 120 }}>
                        {a.site?.name || <span style={{ color: 'var(--color-tx-m)' }}>Unknown</span>}
                      </td>
                      <td style={{ padding: 8 }}><SeverityBadge severity={a.severity} /></td>
                      <td style={{ padding: 8 }}><StatusBadge status={a.status} /></td>
                      <td style={{ padding: 8, minWidth: 220 }}>{a.message}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => setOpenId(a._id)}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-accent, #2ECC71)', whiteSpace: 'nowrap' }}
                        >
                          View <ArrowRight size={11} style={{ verticalAlign: 'middle' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--color-tx-m)' }}>
                      No alerts matching the current filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 14 }}>
            <button type="button" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <span style={{ padding: '6px 12px', color: 'var(--color-tx-m)', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
              Page {page} of {Math.max(1, Math.ceil(listQ.data.meta.total / listQ.data.meta.page_size))}
            </span>
            <button type="button" disabled={page * listQ.data.meta.page_size >= listQ.data.meta.total} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </>
      )}

      {openId && <AlertDetailDrawer id={openId} onClose={closeDrawer} onNavigate={onNavigate} />}
    </div>
  );
}

function AlertDetailDrawer({ id, onClose, onNavigate }: {
  id: string;
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  const qc = useQueryClient();
  const [resolveNote, setResolveNote] = useState('');
  const [showResolve, setShowResolve] = useState(false);

  const detailQ = useQuery({
    queryKey: ['alert', id],
    queryFn: () => fetchAlertDetail(id),
  });
  const ackM = useMutation({
    mutationFn: () => acknowledgeAlert(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alert', id] });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
  const resM = useMutation({
    mutationFn: () => resolveAlert(id, resolveNote),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alert', id] });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
      setShowResolve(false);
      setResolveNote('');
    },
  });

  const alert = detailQ.data?.alert;
  const zone = alert?.site?.timezone;
  const at = (value: string | null | undefined) => formatAlertTime(value, zone).text;
  // Notified, acknowledged, resolved, escalated: "Never" once resolved, "Not yet" before.
  const stage = (value: string | null | undefined) => formatAlertLifecycleTime(value, zone, alert?.status);

  return (
    <div
      role="dialog"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.55)',
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 90vw)', height: '100%',
          background: 'var(--color-sf, #111)', borderLeft: '1px solid var(--color-brd, var(--evi-border))',
          padding: 24, overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Alert detail</h2>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>

        {detailQ.isLoading && <p><Loader2 size={14} className="spin" /> Loading…</p>}
        {detailQ.isError && (
          <p style={{ color: 'var(--evi-error, #ef4444)' }}>
            <AlertTriangle size={14} /> Could not load this alert.
          </p>
        )}
        {detailQ.data && alert && (
          <>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
              <SeverityBadge severity={alert.severity} />
              <StatusBadge status={alert.status} />
            </div>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{alertRuleName(alert.rule_id)}</p>
            <p style={{ margin: '4px 0 0', fontSize: 13 }}>{alert.message}</p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
              {alertChargerAndSite(alert)}
              {alert.charger && (
                <>
                  {' · '}
                  <a
                    href={`/chargers/${alert.charger_id}`}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                      e.preventDefault();
                      onNavigate(`/chargers/${alert.charger_id}`);
                    }}
                  >
                    Open charger
                  </a>
                </>
              )}
            </p>

            <dl style={{ display: 'grid', gridTemplateColumns: 'var(--admin-definition-columns, 160px 1fr)', gap: 6, fontSize: 12, marginTop: 16, fontVariantNumeric: 'tabular-nums' }}>
              <dt style={{ color: 'var(--color-tx-m)' }}>Detected</dt><dd>{at(alert.detected_at)}</dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Notified</dt><dd>{stage(alert.notified_at)}</dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Acknowledged</dt><dd>{stage(alert.acknowledged_at)}</dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Resolved</dt><dd>{stage(alert.resolved_at)}</dd>
              <dt style={{ color: 'var(--color-tx-m)' }}>Escalated</dt><dd>{stage(alert.escalated_at)}</dd>
            </dl>
            {siteZoneMissing(alert.site) && (
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-tx-m)' }}>
                This site has no time zone set, so times are shown in UTC.
              </p>
            )}

            <h3 style={{ marginTop: 20, marginBottom: 6, fontSize: 13 }}>Trigger payload</h3>
            <pre style={{
              margin: 0, fontSize: 11, padding: 10,
              background: 'var(--color-sf2, #1a1a1a)', borderRadius: 6,
              maxHeight: 200, overflow: 'auto',
            }}>
              {JSON.stringify(alert.trigger_payload, null, 2)}
            </pre>

            <h3 style={{ marginTop: 20, marginBottom: 6, fontSize: 13 }}>Agent actions ({detailQ.data.agent_actions.length})</h3>
            {detailQ.data.agent_actions.length === 0
              ? <p style={{ fontSize: 12, color: 'var(--color-tx-m)' }}>None yet.</p>
              : (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 11 }}>
                  {detailQ.data.agent_actions.slice(0, 10).map((a) => (
                    <li key={a._id} style={{ padding: '6px 0', borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                      <code>{a.action_type}</code> · {a.outcome} · {at(a.timestamp)}
                    </li>
                  ))}
                </ul>
              )}

            {/* Actions */}
            <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
              {(alert.status === 'detected' || alert.status === 'notified') && (
                <button
                  type="button"
                  onClick={() => ackM.mutate()}
                  disabled={ackM.isPending}
                  style={{
                    padding: '8px 14px', fontSize: 13,
                    background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8,
                    cursor: 'pointer', fontWeight: 600,
                  }}
                >
                  Acknowledge
                </button>
              )}
              {alert.status !== 'resolved' && (
                <button
                  type="button"
                  onClick={() => setShowResolve((v) => !v)}
                  style={{
                    padding: '8px 14px', fontSize: 13,
                    background: 'transparent', color: 'var(--evi-accent, #2ECC71)',
                    border: '1px solid var(--evi-accent, #2ECC71)', borderRadius: 8,
                    cursor: 'pointer',
                  }}
                >
                  <CheckCircle2 size={11} style={{ verticalAlign: 'middle' }} /> Resolve
                </button>
              )}
            </div>

            {showResolve && (
              <div style={{ marginTop: 12 }}>
                <textarea
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                  placeholder="Resolution note (required)"
                  rows={3}
                  style={{ width: '100%', padding: 8, fontSize: 12 }}
                />
                <button
                  type="button"
                  disabled={resolveNote.length < 3 || resM.isPending}
                  onClick={() => resM.mutate()}
                  style={{
                    marginTop: 6, padding: '8px 14px', fontSize: 13,
                    background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8,
                    cursor: resolveNote.length < 3 ? 'not-allowed' : 'pointer',
                  }}
                >
                  Confirm resolve
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
